import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import type { AssetTemplate, AssetTemplateCreateInput } from '@/types/assetTemplate';
import { fetchAllAssetCategories } from '../lib/assetCategories';
import {
  AXIS_SHORT,
  buildProductDimensions,
  type DimensionAxis,
  type DimensionFeatureConfig,
  parseDimensionConfig,
  parseProductDimensions,
  validateProductDimensions,
} from '../lib/productDimensions';
import { TEMPLATE_DEFAULT_CONDITION_VALUES } from '../constants/assetConditions';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';

interface AssetTemplateFormProps {
  template?: AssetTemplate | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const templateSchema = z.object({
  name: z.string().min(1, 'Template name is required'),
  description: z.string().optional(),
  category_id: z.string().optional(),
  default_condition: z.enum(TEMPLATE_DEFAULT_CONDITION_VALUES),
  is_active: z.boolean(),
  warehouse_stock: z.number().int().min(0).nullable().optional(),
  specifications: z.record(z.any()).optional(),
});

type TemplateFormData = z.infer<typeof templateSchema>;

const AssetTemplateForm: React.FC<AssetTemplateFormProps> = ({
  template,
  onSuccess,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [specifications, setSpecifications] = useState<{ key: string; value: string }[]>([]);
  const [categoryFeatures, setCategoryFeatures] = useState<{
    id: string;
    category_id: string;
    feature_name: string;
    feature_type: 'text' | 'number' | 'boolean' | 'select' | 'dimension';
    description?: string | null;
    options?: string[] | DimensionFeatureConfig | null;
    is_required: boolean;
  }[]>([]);
  const [featureValues, setFeatureValues] = useState<Record<string, string | number | boolean>>({});
  const [dimensionValues, setDimensionValues] = useState<Partial<Record<DimensionAxis, number | ''>>>({});
  const [step, setStep] = useState(0);
  const steps = ['Basic Info', 'Features', 'Review'];

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TemplateFormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: template?.name || '',
      description: template?.description || '',
      category_id: template?.category_id || '',
      default_condition: template?.default_condition || 'new',
      is_active: template?.is_active ?? true,
      warehouse_stock: template?.warehouse_stock ?? undefined,
      specifications: template?.specifications || {},
    },
  });

  const categoryId = watch('category_id');

  useEffect(() => {
    fetchAllAssetCategories(true)
      .then(setCategories)
      .catch((err) => console.error('Error loading categories:', err));
  }, []);

  useEffect(() => {
    if (!template?.category_id || !template.category) return;
    setCategories((prev) => {
      if (prev.some((c) => c.id === template.category_id)) return prev;
      return [...prev, {
        id: template.category!.id,
        name: template.category!.name,
        description: template.category!.description,
      }].sort((a, b) => a.name.localeCompare(b.name));
    });
  }, [template?.category_id, template?.category?.id]);

  const dimensionFeature = categoryFeatures.find((f) => f.feature_type === 'dimension');
  const dimensionConfig = parseDimensionConfig(dimensionFeature?.options);

  useEffect(() => {
    if (template?.specifications) {
      const reserved = new Set(categoryFeatures.map((f) => toSnakeCase(f.feature_name)));
      reserved.add('dimensions');
      const specs = Object.entries(template.specifications)
        .filter(([key]) => !reserved.has(key))
        .map(([key, value]) => ({ key, value: typeof value === 'object' ? JSON.stringify(value) : String(value) }));
      setSpecifications(specs);
      if (template.category_id) setValue('category_id', template.category_id);
    } else {
      setSpecifications([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, setValue, categoryFeatures.length]);

  // Load category features when category changes
  useEffect(() => {
    if (categoryId) loadCategoryFeatures(categoryId);
    else setCategoryFeatures([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const loadCategoryFeatures = async (catId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/asset-categories/${catId}/features`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const features = Array.isArray(data) ? data : (data.data || data);
        setCategoryFeatures(features);
        // Initialize defaults or existing values from template.specifications
        const initial: Record<string, string | number | boolean> = {};
        features.forEach((f: any) => {
          if (f.feature_type === 'dimension') return;
          const key = toSnakeCase(f.feature_name);
          const existing = (template?.specifications as any)?.[key];
          if (existing !== undefined && existing !== null) initial[key] = existing;
          else if (f.feature_type === 'boolean') initial[key] = false;
          else initial[key] = '' as any;
        });
        setFeatureValues(initial);

        const dimConfig = parseDimensionConfig(features.find((f: any) => f.feature_type === 'dimension')?.options);
        if (dimConfig) {
          const existing = parseProductDimensions(template?.specifications as Record<string, unknown>);
          const initialDims: Partial<Record<DimensionAxis, number | ''>> = {};
          dimConfig.axes.forEach((axis) => {
            initialDims[axis] = existing?.[axis] ?? '';
          });
          setDimensionValues(initialDims);
        } else {
          setDimensionValues({});
        }

        if (template?.specifications) {
          const keys = new Set(features.map((f: any) => toSnakeCase(f.feature_name)));
          keys.add('dimensions');
          const extras = Object.entries(template.specifications)
            .filter(([k]) => !keys.has(k))
            .map(([k, v]) => ({ key: k, value: typeof v === 'object' ? JSON.stringify(v) : String(v) }));
          setSpecifications(extras);
        }
      }
    } catch (err) {
      console.error('Error loading category features:', err);
    }
  };

  const toSnakeCase = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  // Images moved to Product Details → Media tab

  const reorderImages = async (updated: Array<{ id: string; position: number }>) => {
    if (!template?.id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/asset-templates/${template.id}/images/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order: updated }),
      });
      if (!res.ok) throw new Error('Reorder failed');
    } catch (err) {
      console.error('Reorder error:', err);
    }
  };

  const addSpecification = () => {
    setSpecifications([...specifications, { key: '', value: '' }]);
  };

  const updateSpecification = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...specifications];
    updated[index][field] = value;
    setSpecifications(updated);
  };

  const removeSpecification = (index: number) => {
    setSpecifications(specifications.filter((_, i) => i !== index));
  };

  const submitForm = async (data: TemplateFormData) => {
    try {
      setLoading(true);

      // Convert specifications array to object
      const featureKeys = new Set(
        categoryFeatures.filter((f) => f.feature_type !== 'dimension').map((f) => toSnakeCase(f.feature_name)),
      );
      const specsObject = specifications.reduce((acc, spec) => {
        const key = spec.key.trim();
        if (key && key !== 'dimensions' && !featureKeys.has(key)) {
          acc[key] = spec.value.trim();
        }
        return acc;
      }, {} as Record<string, any>);

      const mergedSpecs: Record<string, any> = { ...(template?.specifications ?? {}), ...specsObject };

      categoryFeatures.forEach((f) => {
        if (f.feature_type === 'dimension') return;
        const key = toSnakeCase(f.feature_name);
        const val = featureValues[key];
        const normalized = f.feature_type === 'boolean' ? Boolean(val) : val;
        if (f.is_required && (normalized === '' || normalized === undefined || normalized === null)) {
          throw new Error(`Missing required feature: ${f.feature_name}`);
        }
        if (normalized !== undefined && normalized !== '') mergedSpecs[key] = normalized;
      });

      if (dimensionConfig) {
        const dimErr = validateProductDimensions(dimensionValues, dimensionConfig);
        if (dimErr && dimensionFeature?.is_required) throw new Error(dimErr);
        const built = buildProductDimensions(dimensionValues, dimensionConfig);
        const hasAny = dimensionConfig.axes.some((a) => built[a] !== undefined);
        if (hasAny) mergedSpecs.dimensions = built;
        else delete mergedSpecs.dimensions;
      }

      const payload: AssetTemplateCreateInput = {
        name: data.name,
        description: data.description || undefined,
        category_id: data.category_id || undefined,
        default_condition: data.default_condition,
        is_active: data.is_active,
        warehouse_stock: data.warehouse_stock ?? null,
        specifications: Object.keys(mergedSpecs).length > 0 ? mergedSpecs : undefined,
      };

      const url = template
        ? `/api/asset-templates/${template.id}`
        : '/api/asset-templates';

      const method = template ? 'PUT' : 'POST';

      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save template');
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving template:', err);
      window.alert(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const defaultCondition = watch('default_condition');
  const isActive = watch('is_active');

  const onInvalid = (formErrors: any) => {
    const step0Fields = ['name', 'category_id', 'default_condition'];
    if (step0Fields.some((f) => formErrors[f])) { setStep(0); return; }
    const anyMsg = (Object.values(formErrors) as any[]).find((e: any) => e && (e as any).message)?.message;
    if (anyMsg) window.alert(anyMsg);
  };

  return (
    <form onSubmit={handleSubmit(submitForm, onInvalid)} className="space-y-6">
      {/* Wizard header */}
      <div className="flex items-center gap-3 text-sm">
        {steps.map((label, idx) => (
          <div key={label} className="flex items-center">
            <div className={`h-6 w-6 rounded-full flex items-center justify-center text-white text-xs ${idx <= step ? 'bg-primary' : 'bg-gray-300'}`}>{idx + 1}</div>
            <span className={`ml-2 ${idx === step ? 'font-semibold' : 'text-gray-500'}`}>{label}</span>
            {idx < steps.length - 1 && <span className="mx-3 text-gray-400">›</span>}
          </div>
        ))}
      </div>

      {/* Step 0: Basic info */}
      {step === 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="name">Product Name *</Label>
              <Input id="name" {...register('name')} placeholder="e.g., Alegra Armchair" className={errors.name ? 'border-red-500' : ''} />
              {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>}
            </div>
            <div>
              <Label htmlFor="category_id">Category</Label>
              <Select value={categoryId || 'none'} onValueChange={(value) => setValue('category_id', value === 'none' ? '' : value)}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Category</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="default_condition">Default Condition *</Label>
              <Select value={defaultCondition} onValueChange={(value) => setValue('default_condition', value as any)}>
                <SelectTrigger><SelectValue placeholder="Select condition" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="pre_loved">Pre-Loved</SelectItem>
                  <SelectItem value="damaged">Damaged</SelectItem>
                  <SelectItem value="missing">Missing</SelectItem>
                </SelectContent>
              </Select>
              {errors.default_condition && <p className="text-sm text-red-600 mt-1">{errors.default_condition.message}</p>}
            </div>
            <div>
              <Label htmlFor="warehouse_stock">Warehouse Stock</Label>
              <Input
                id="warehouse_stock"
                type="number"
                min={0}
                step={1}
                {...register('warehouse_stock', {
                  setValueAs: (v) => (v === '' || v == null ? null : parseInt(String(v), 10)),
                })}
                placeholder="Units in warehouse"
              />
              {errors.warehouse_stock && <p className="text-sm text-red-600 mt-1">{errors.warehouse_stock.message}</p>}
            </div>
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" {...register('description')} placeholder="Optional description of this product..." rows={3} />
          </div>
        </>
      )}

      {/* Images step removed. Manage images in Media tab on Product Details page. */}

      {/* Step 1: Features & Specs */}
      {step === 1 && (
        <div>
          <div className="mb-4">
            <Label>Category Features</Label>
            {categoryId ? (
              <div className="mt-2 space-y-3">
                {categoryFeatures.length === 0 && (<p className="text-sm text-gray-500">No features defined for this category.</p>)}
                {categoryFeatures.map((f) => {
                  if (f.feature_type === 'dimension' && dimensionConfig) {
                    return (
                      <div key={f.id} className="space-y-3 border rounded-md p-3">
                        <Label>{f.feature_name}{f.is_required ? ' *' : ''}</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {dimensionConfig.axes.map((axis) => (
                            <div key={axis}>
                              <Label htmlFor={`dim-${axis}`} className="text-xs text-muted-foreground">
                                {AXIS_SHORT[axis]} ({axis}) — {dimensionConfig.unit}
                              </Label>
                              <Input
                                id={`dim-${axis}`}
                                type="number"
                                min={0}
                                step="any"
                                value={dimensionValues[axis] ?? ''}
                                onChange={(e) => setDimensionValues((v) => ({
                                  ...v,
                                  [axis]: e.target.value === '' ? '' : Number(e.target.value),
                                }))}
                              />
                            </div>
                          ))}
                        </div>
                        {f.description && (<p className="text-xs text-gray-500">{f.description}</p>)}
                      </div>
                    );
                  }
                  const key = toSnakeCase(f.feature_name);
                  const commonProps = { id: key, name: key } as const;
                  return (
                    <div key={f.id} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                      <Label htmlFor={key} className="md:col-span-1">{f.feature_name}{f.is_required ? ' *' : ''}</Label>
                      <div className="md:col-span-2">
                        {f.feature_type === 'boolean' ? (
                          <div className="flex items-center space-x-2">
                            <Checkbox checked={Boolean(featureValues[key])} onCheckedChange={(checked) => setFeatureValues(v => ({ ...v, [key]: !!checked }))} />
                            <span className="text-sm text-gray-600">Yes</span>
                          </div>
                        ) : f.feature_type === 'number' ? (
                          <Input type="number" {...commonProps} value={String(featureValues[key] ?? '')} onChange={(e) => setFeatureValues(v => ({ ...v, [key]: e.target.value === '' ? '' : Number(e.target.value) }))} />
                        ) : f.feature_type === 'select' ? (
                          <Select value={String(featureValues[key] ?? '')} onValueChange={(val) => setFeatureValues(v => ({ ...v, [key]: val }))}>
                            <SelectTrigger><SelectValue placeholder={`Select ${f.feature_name.toLowerCase()}`} /></SelectTrigger>
                            <SelectContent>{(Array.isArray(f.options) ? f.options : []).map((opt) => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}</SelectContent>
                          </Select>
                        ) : f.feature_type !== 'dimension' ? (
                          <Input type="text" {...commonProps} value={String(featureValues[key] ?? '')} onChange={(e) => setFeatureValues(v => ({ ...v, [key]: e.target.value }))} placeholder={`Enter ${f.feature_name.toLowerCase()}`} />
                        ) : null}
                        {f.description && (<p className="text-xs text-gray-500 mt-1">{f.description}</p>)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Select a category to view its features.</p>
            )}
          </div>

          <div className="flex items-center justify-between mb-3">
            <Label>Additional Specifications</Label>
            <Button type="button" variant="outline" size="sm" onClick={addSpecification}>Add Specification</Button>
          </div>
          <div className="space-y-3">
            {specifications.map((spec, index) => (
              <div key={index} className="flex gap-2 items-center">
                <Input placeholder="Property name" value={spec.key} onChange={(e) => updateSpecification(index, 'key', e.target.value)} className="flex-1" />
                <Input placeholder="Value" value={spec.value} onChange={(e) => updateSpecification(index, 'value', e.target.value)} className="flex-1" />
                <Button type="button" variant="outline" size="sm" onClick={() => removeSpecification(index)}>Remove</Button>
              </div>
            ))}
            {specifications.length === 0 && (
              <p className="text-sm text-gray-500 italic">No additional specifications. You can add custom properties if needed.</p>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Review & Status */}
      {step === 2 && (
        <div className="flex items-center space-x-2">
          <Checkbox id="is_active" checked={isActive} onCheckedChange={(checked) => setValue('is_active', !!checked)} />
          <Label htmlFor="is_active">Active product (available for creating units)</Label>
        </div>
      )}

      {/* Wizard actions */}
      <div className="flex justify-between">
        <div>{step > 0 && (<Button type="button" variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))}>Back</Button>)}</div>
        <div className="space-x-2">
          <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
          {step < steps.length - 1 ? (
            <Button type="button" onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}>Next</Button>
          ) : (
            <Button
              type="button"
              disabled={loading}
              onClick={() => {
                try { console.log('Submitting AssetTemplateForm'); } catch {}
                return handleSubmit(submitForm, (e) => {
                  try { console.log('AssetTemplateForm invalid', e); } catch {}
                  return (onInvalid as any)(e);
                })();
              }}
            >
              {loading ? 'Saving...' : (template ? 'Update Product' : 'Create Product')}
            </Button>
          )}
        </div>
      </div>
    </form>
  );
};

export default AssetTemplateForm;
